import {useState, useCallback, useEffect, useContext} from "react";
import {IModuleScheduled} from "@buildwitt/shapeshift-types";
import {useApi} from "@buildwitt/shapeshift-identity";
import {StrapiErrorResponse} from "@buildwitt/shapeshift-identity/types/IAPI";
import {AuthContext} from "@buildwitt/shapeshift-identity";

interface ApiResult {
	isLoading: boolean;
	isError: boolean;
	statusCode?: number;
	error?: StrapiErrorResponse;
}

interface UseScheduledModulesResult {
	scheduledModules: IModuleScheduled[] | null;
	apiResult: ApiResult;
	getScheduledModules: (startDate: Date, endDate: Date, locale: string, options?: { throttle?: boolean; cache?: boolean }) => Promise<void>;
}

const useCustomScheduledModules = (): UseScheduledModulesResult => {
	const [scheduledModules, setScheduledModules] = useState<IModuleScheduled[] | null>(null);
	const [apiResponse, submitApiCall] = useApi<IModuleScheduled[]>();
	const {jwt} = useContext(AuthContext);
	const getScheduledModules = useCallback(async (
		startDate: Date,
		endDate: Date,
		locale: string,
		options = {throttle: true, cache: true}
	) => {
		submitApiCall({
			url: "/api/modules/scheduled",
			method: "GET",
			config: {
				params: {
					startDate: startDate.toISOString(),
					endDate: endDate.toISOString(),
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					locale: locale || null
				},
				headers: {
					"Authorization": `Bearer ${jwt}`,
					"Content-Type": "application/json"
				}
			},
			cache: options.cache,
			throttle: options.throttle
		});
	}, [submitApiCall, jwt]);

	useEffect(() => {
		if (apiResponse.statusCode === 200 && apiResponse.data) {
			setScheduledModules(apiResponse.data);
		} else if (apiResponse.statusCode && apiResponse.statusCode !== 200) {
			setScheduledModules(null);
		}
	}, [apiResponse]);

	return {
		scheduledModules,
		apiResult: {
			isLoading: apiResponse.isLoading,
			isError: apiResponse.isError,
			statusCode: apiResponse.statusCode,
			error: apiResponse.errorData
		},
		getScheduledModules
	};
};

export default useCustomScheduledModules;